/**
 * Line Summary modal (2026-05-16).
 *
 * Compact overview shown when the user clicks the line header in the factory
 * overview. Different from the existing `openLineDetail` modal — that one
 * deep-dives into the main machine; this one is a quick line-level summary
 * with operating hours (independent from main machine), live consumption and
 * a short list of all machines on the line.
 *
 * Master users see an "Editează ore linie" button that POSTs to
 * /api/lines/<id>/hours and flips the source to 'manual'.
 */

import { state, api, $, toast, escHtml, formatDateTimeRO } from './core';
import { askText, askConfirm } from './dialogs';

declare const openLineDetail: any;
declare const openLineLayoutDetail: any;

interface LineSummaryMachine {
    id: string;
    name: string;
    machine_type: string | null;
    is_main: boolean;
    operating_hours: number;
    power_kw: number;
    status: string;
    manufacturer?: string;
    model?: string;
}

interface LineSummary {
    line_id: string;
    product: string;
    group_type: string;
    icon: string;
    is_active: number;
    started_at: string | null;
    status: 'production' | 'stopped' | 'warning' | 'off';
    operating_hours: number;
    operating_hours_source: string;
    manual_hours_set_at: string | null;
    manual_hours_set_by: string | null;
    last_active_at: string | null;
    current_power_kw: number;
    main_machine_power_kw: number;
    power_threshold_kw: number;
    main_machine_id: string | null;
    machines_count: number;
    machines_active: number;
    machines: LineSummaryMachine[];
}

const STATUS_LABEL: Record<string, string> = {
    production: 'PRODUCȚIE',
    stopped: 'OPRITĂ',
    warning: 'PARȚIAL',
    off: 'INACTIVĂ',
};

const STATUS_COLOR: Record<string, string> = {
    production: '#16a34a',
    stopped: '#dc2626',
    warning: '#f59e0b',
    off: '#64748b',
};

function _machineStatusBadge(s: string): string {
    const map: Record<string, { lbl: string, bg: string }> = {
        running: { lbl: 'RULEAZĂ', bg: '#16a34a' },
        ok: { lbl: 'OK', bg: '#475569' },
        warning: { lbl: 'ATENȚIE', bg: '#f59e0b' },
        stopped: { lbl: 'OPRIT', bg: '#dc2626' },
        off: { lbl: 'OFF', bg: '#64748b' },
    };
    const cfg = map[s] || map.ok;
    return `<span style="display:inline-block;padding:1px 7px;border-radius:6px;background:${cfg.bg};color:#fff;font-size:.62rem;font-weight:700;letter-spacing:.4px">${cfg.lbl}</span>`;
}

function _machineIcon(mtype: string | null, isMain: boolean): string {
    if (isMain) return '⭐';
    switch (mtype) {
        case 'injection':
        case 'compression':
            return '🏭';
        case 'mold':
            return '🧩';
        case 'chiller':
            return '❄️';
        case 'compressor':
            return '🌀';
        default:
            return '⚙️';
    }
}

export async function openLineSummary(lineId: string): Promise<void> {
    if (!lineId) return;
    const existing = document.getElementById('line-summary-overlay');
    if (existing) existing.remove();

    const overlay = document.createElement('div');
    overlay.id = 'line-summary-overlay';
    overlay.className = 'proc-overlay';
    overlay.innerHTML = `<div class="proc-container" style="max-width:760px;overflow-y:auto">
        <div class="proc-header"><div class="proc-header-top">
            <h2 class="proc-title" id="ls-title">🏭 Sumar Linie</h2>
            <button class="proc-close" id="ls-close" aria-label="Închide">&times;</button>
        </div></div>
        <div class="proc-body" id="ls-body" style="padding:14px">
            <div style="text-align:center;padding:40px;color:var(--text-muted)">
                <div class="spinner"></div> Se încarcă sumarul liniei…
            </div>
        </div>
    </div>`;
    document.body.appendChild(overlay);

    const close = () => overlay.remove();
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) close();
    });
    document.getElementById('ls-close')?.addEventListener('click', close);

    let data: LineSummary;
    try {
        data = await api(`/api/lines/${encodeURIComponent(lineId)}/summary`);
    } catch (e: any) {
        const body = document.getElementById('ls-body');
        if (body) body.innerHTML = `<div class="alarm-empty"><span class="icon">⚠</span>${escHtml(e?.message || 'Eroare la încărcarea sumarului')}</div>`;
        return;
    }
    if (!data || (data as any).error) {
        const body = document.getElementById('ls-body');
        if (body) body.innerHTML = `<div class="alarm-empty">${escHtml((data as any)?.error || 'Răspuns invalid')}</div>`;
        return;
    }

    _renderLineSummary(data);
}

function _renderLineSummary(d: LineSummary): void {
    const title = document.getElementById('ls-title');
    const body = document.getElementById('ls-body');
    if (!title || !body) return;

    title.innerHTML = `🏭 ${escHtml(d.line_id)} — ${escHtml(d.product || '—')}`;

    const statusLbl = STATUS_LABEL[d.status] || d.status.toUpperCase();
    const statusColor = STATUS_COLOR[d.status] || '#475569';

    const sourceLabel = d.operating_hours_source === 'manual'
        ? 'MANUAL'
        : d.operating_hours_source === 'aggregate'
            ? 'AGREGAT'
            : 'AUTO (sincron cu mașina principală)';
    const sourceBadge = d.operating_hours_source === 'manual'
        ? `<span title="Ore setate manual la ${escHtml(formatDateTimeRO(d.manual_hours_set_at))} de ${escHtml(d.manual_hours_set_by || 'necunoscut')}" style="margin-left:6px;padding:1px 6px;border-radius:5px;background:#f59e0b;color:#0a0f1c;font-size:.6rem;font-weight:700">MANUAL</span>`
        : `<span title="Sincronizate automat cu ore mașină principală" style="margin-left:6px;padding:1px 6px;border-radius:5px;background:#0ea5e9;color:#0a0f1c;font-size:.6rem;font-weight:700">AUTO</span>`;

    const startedRow = d.started_at
        ? `<div class="ls-metric"><span class="ls-metric-icon">📅</span><span class="ls-metric-label">GO-LIVE</span><span class="ls-metric-value">${escHtml(formatDateTimeRO(d.started_at))}</span></div>`
        : `<div class="ls-metric"><span class="ls-metric-icon">📅</span><span class="ls-metric-label">GO-LIVE</span><span class="ls-metric-value" style="color:var(--text-muted)">— nepornită oficial</span></div>`;

    const isMaster = (state.currentUser?.role || '') === 'master';

    const machinesHtml = (d.machines || []).map(m => {
        const icon = _machineIcon(m.machine_type, m.is_main);
        const badge = _machineStatusBadge(m.status);
        const mainTag = m.is_main ? '<span style="color:#fbbf24;margin-left:4px" title="Mașină principală">★</span>' : '';
        const hours = m.operating_hours != null ? `${m.operating_hours.toFixed(1)} h` : '— h';
        const power = m.power_kw != null ? `${m.power_kw.toFixed(2)} kW` : '— kW';
        const safeName = escHtml(m.name || m.id);
        const safeId = escHtml(m.id);
        return `<div class="ls-machine-row" data-machine-id="${safeId}">
            <span class="ls-machine-icon">${icon}</span>
            <span class="ls-machine-id">${safeId}${mainTag}</span>
            <span class="ls-machine-name" title="${safeName}">${safeName}</span>
            <span class="ls-machine-hours" title="Ore funcționare">⏱ ${hours}</span>
            <span class="ls-machine-power" title="Consum live">⚡ ${power}</span>
            <span class="ls-machine-status">${badge}</span>
        </div>`;
    }).join('');

    const lastActiveHtml = d.last_active_at
        ? `<span style="color:var(--text-muted);font-size:.7rem;margin-left:6px">(ultima activitate: ${escHtml(formatDateTimeRO(d.last_active_at))})</span>`
        : '';

    body.innerHTML = `
        <div class="ls-status-banner" style="background:${statusColor};color:#fff;padding:8px 14px;border-radius:8px;font-weight:700;letter-spacing:.6px;text-align:center;margin-bottom:12px">
            ${escHtml(statusLbl)}${lastActiveHtml}
        </div>

        <div class="ls-metrics-grid">
            <div class="ls-metric">
                <span class="ls-metric-icon">⏱</span>
                <span class="ls-metric-label">Ore linie</span>
                <span class="ls-metric-value">${d.operating_hours.toFixed(1)} h ${sourceBadge}</span>
            </div>
            <div class="ls-metric">
                <span class="ls-metric-icon">⚡</span>
                <span class="ls-metric-label">Consum live</span>
                <span class="ls-metric-value">${d.current_power_kw.toFixed(2)} kW</span>
            </div>
            <div class="ls-metric">
                <span class="ls-metric-icon">🏭</span>
                <span class="ls-metric-label">Utilaje active</span>
                <span class="ls-metric-value">${d.machines_active} / ${d.machines_count}</span>
            </div>
            ${startedRow}
        </div>

        <div class="ls-source-row" style="margin-top:6px;font-size:.72rem;color:var(--text-muted)">
            Sursă ore: <strong style="color:var(--text-main)">${escHtml(sourceLabel)}</strong>
            ${d.manual_hours_set_at ? ` · setat ${escHtml(formatDateTimeRO(d.manual_hours_set_at))}` : ''}
            ${d.manual_hours_set_by ? ` de ${escHtml(d.manual_hours_set_by)}` : ''}
        </div>

        ${isMaster ? `
        <div class="ls-master-actions" style="margin-top:10px;display:flex;gap:8px;flex-wrap:wrap">
            <button type="button" class="btn btn-sm btn-outline" id="ls-edit-hours" title="Setează manual orele liniei (nu afectează mașina principală)">✏ Editează ore linie</button>
            ${d.operating_hours_source === 'manual' ? `<button type="button" class="btn btn-sm btn-outline" id="ls-clear-manual" title="Renunță la override manual — orele liniei vor reveni la sincron cu mașina principală">↻ Revino la AUTO</button>` : ''}
        </div>` : ''}

        <h3 style="margin:14px 0 6px;font-size:.85rem;letter-spacing:.4px">UTILAJE PE LINIE</h3>
        <div class="ls-machines-list">${machinesHtml || '<div style="color:var(--text-muted);font-size:.8rem">Nicio mașină asignată acestei linii.</div>'}</div>

        <div class="ls-footer" style="margin-top:14px;display:flex;gap:8px;flex-wrap:wrap;border-top:1px solid var(--border);padding-top:10px">
            ${d.main_machine_id ? `<button type="button" class="btn btn-sm btn-primary" id="ls-open-main" title="Deschide modal detaliu pentru mașina principală">⭐ Mașina principală</button>` : ''}
            <button type="button" class="btn btn-sm btn-outline" id="ls-open-layout" title="Vezi layout linie">🗺 Layout linie</button>
            <button type="button" class="btn btn-sm btn-outline" id="ls-close-btn" style="margin-left:auto">Închide</button>
        </div>
    `;

    document.getElementById('ls-close-btn')?.addEventListener('click', () => {
        document.getElementById('line-summary-overlay')?.remove();
    });

    document.getElementById('ls-open-main')?.addEventListener('click', () => {
        if (!d.main_machine_id) return;
        document.getElementById('line-summary-overlay')?.remove();
        try {
            // butonul promite mașina PRINCIPALĂ — nu linia (audit UX 30 iul 2026)
            const w = window as any;
            if (typeof w.openMachinePanel === 'function') w.openMachinePanel(d.main_machine_id);
            else w.openLineDetail?.(d.main_machine_id, d.product || '', state.currentUser?.role || 'operator');
        } catch (_) { /* defensive */ }
    });

    document.getElementById('ls-open-layout')?.addEventListener('click', () => {
        document.getElementById('line-summary-overlay')?.remove();
        try {
            (window as any).openLineLayoutDetail?.(d.line_id, d.product || '');
        } catch (_) { /* defensive */ }
    });

    document.querySelectorAll<HTMLElement>('.ls-machine-row[data-machine-id]').forEach((row) => {
        row.addEventListener('click', () => {
            const mid = row.dataset.machineId || '';
            if (!mid) return;
            document.getElementById('line-summary-overlay')?.remove();
            try {
                const role = state.currentUser?.role || 'operator';
                (window as any).openLineDetail?.(mid, d.product || '', role);
            } catch (_) { /* defensive */ }
        });
    });

    if (isMaster) {
        document.getElementById('ls-edit-hours')?.addEventListener('click', async () => {
            const current = d.operating_hours.toFixed(1);
            const input = await askText(`Ore funcționare LINIE ${d.line_id}`, {
                label: `${d.product}\n\nAtenție: valoarea e INDEPENDENTĂ de orele mașinii principale.\n` +
                    `După salvare, sincronizarea automată se oprește (sursa devine „manual").\n\n` +
                    `Valoare curentă: ${current} h`,
                value: current, inputMode: 'decimal', required: true,
            });
            if (input == null) return;
            const val = parseFloat(input);
            if (!Number.isFinite(val) || val < 0) {
                toast('Valoare invalidă', 'error');
                return;
            }
            try {
                await api(`/api/lines/${encodeURIComponent(d.line_id)}/hours`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ operating_hours: val, auto_hours_source: 'manual' }),
                });
                toast(`Ore linie ${d.line_id} setate la ${val.toFixed(1)} h (sursă: manual)`);
                openLineSummary(d.line_id);
                try { (window as any).loadOverview?.(); } catch (_) { /* defensive */ }
            } catch (e: any) {
                toast(`Eroare: ${e?.message || 'salvare eșuată'}`, 'error');
            }
        });

        document.getElementById('ls-clear-manual')?.addEventListener('click', async () => {
            if (!await askConfirm(`Revino linia ${d.line_id} la sincronizare AUTO cu mașina principală?`, `Valoarea curentă (${d.operating_hours.toFixed(1)} h) va rămâne ca punct de pornire,\ndar va fi rescrisă la următorul tick al propagator-ului.`)) return;
            try {
                await api(`/api/lines/${encodeURIComponent(d.line_id)}/hours`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ operating_hours: d.operating_hours, auto_hours_source: 'main_machine' }),
                });
                toast(`Linia ${d.line_id} revine la sincron auto cu mașina principală`);
                openLineSummary(d.line_id);
                try { (window as any).loadOverview?.(); } catch (_) { /* defensive */ }
            } catch (e: any) {
                toast(`Eroare: ${e?.message || 'operațiune eșuată'}`, 'error');
            }
        });
    }
}
